import React, { useState, useEffect } from 'react';
import { shiftsApi } from '../utils/shiftsApi';
import { driverReportsApi } from '../utils/driverReportsApi';

function Stundenabrechnung() {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [auditLog, setAuditLog] = useState([]);

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      const data = await driverReportsApi.getAllDrivers();
      setDrivers(data || []);
      
      if (data && data.length > 0) {
        setSelectedDriver(data[0].driver_id);
      }
    } catch (err) {
      console.error('Error loading drivers:', err);
    }
  };

  const loadReport = async () => {
    if (!selectedDriver) {
      setMessage({ type: 'error', text: 'Bitte Fahrer wählen' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);
      const shiftData = await shiftsApi.getMonthShifts(selectedDriver, year, month);
      setReport(shiftData);
      
      // Collect all edit logs from shifts
      const allEditLogs = [];
      if (shiftData && shiftData.shifts) {
        shiftData.shifts.forEach(shift => {
          if (shift.edit_logs && shift.edit_logs.length > 0) {
            shift.edit_logs.forEach(log => {
              allEditLogs.push({
                ...log,
                shift_id: shift.id,
                shift_date: shift.date
              });
            });
          }
        });
      }
      setAuditLog(allEditLogs);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEditShift = (shift) => {
    setEditingShift({
      ...shift,
      shift_start: shift.shift_start.split('T')[0] + 'T' + shift.shift_start.split('T')[1].substring(0, 5),
      shift_end: shift.shift_end ? shift.shift_end.split('T')[0] + 'T' + shift.shift_end.split('T')[1].substring(0, 5) : '',
      edit_reason: ''
    });
  };

  const saveEdit = async () => {
    if (!editingShift.shift_end) {
      setMessage({ type: 'error', text: 'Bitte Ende-Zeit eingeben' });
      return;
    }

    if (!editingShift.edit_reason) {
      setMessage({ type: 'error', text: 'Bitte Grund für Änderung angeben' });
      return;
    }

    try {
      setLoading(true);
      await shiftsApi.updateShift(editingShift.id, {
        shift_start: editingShift.shift_start + ':00',
        shift_end: editingShift.shift_end + ':00',
        edited_by: 'admin-001',
        edit_reason: editingShift.edit_reason
      });

      setMessage({ type: 'success', text: '✓ Schicht aktualisiert' });
      setEditingShift(null);
      
      // Reload
      setTimeout(() => {
        loadReport();
        setMessage(null);
      }, 1500);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!report) return;

    // CSV Header
    const headers = ['Datum', 'Wochentag', 'Start', 'Ende', 'Stunden', 'Status', 'Notizen'];
    
    // CSV Rows
    const rows = report.shifts.map(shift => {
      const date = new Date(shift.shift_start);
      const status = shift.is_open ? 'Offen' : shift.edit_logs?.length > 0 ? 'Korrigiert' : 'Abgeschlossen';
      const notes = shift.edit_logs?.length > 0 ? shift.edit_logs[shift.edit_logs.length - 1].reason : '';
      
      return [
        date.toLocaleDateString('de-DE'),
        date.toLocaleDateString('de-DE', { weekday: 'long' }),
        date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        shift.shift_end ? new Date(shift.shift_end).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '—',
        shift.duration_hours ? shift.duration_hours + ' h' : '—',
        status,
        notes
      ];
    });

    // Add incomplete shifts
    if (report.incomplete_shifts && report.incomplete_shifts.length > 0) {
      report.incomplete_shifts.forEach(shift => {
        const date = new Date(shift.shift_start);
        rows.push([
          date.toLocaleDateString('de-DE'),
          date.toLocaleDateString('de-DE', { weekday: 'long' }),
          date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
          'OFFEN',
          '—',
          'Unvollständig',
          'Fehlendes Logout'
        ]);
      });
    }

    // Add summary row
    rows.push([]);
    rows.push(['GESAMT', '', '', '', report.total_hours + ' h', '', `${report.shift_count} Schichten`]);

    // Build CSV content
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    // Download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Stundenabrechnung_${report.driver_name}_${report.year}-${String(report.month).padStart(2, '0')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setMessage({ type: 'success', text: '✓ CSV-Datei heruntergeladen' });
    setTimeout(() => setMessage(null), 2000);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Stundenabrechnung
        </h1>
        <p className="text-[var(--text-secondary)] mt-1">Arbeitszeiten & Schichten</p>
      </div>

      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-[var(--success-bg)] text-[var(--success-text)] border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Selection */}
      <div className="dark-card rounded-lg shadow border p-6 mb-6">
        <h2 className="text-lg font-bold mb-4">Monatsbericht erstellen</h2>
        
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Fahrer</label>
            <select
              value={selectedDriver}
              onChange={(e) => setSelectedDriver(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="driver-select"
            >
              <option value="">Bitte wählen</option>
              {drivers.map((driver) => (
                <option key={driver.driver_id} value={driver.driver_id}>
                  {driver.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Jahr</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg"
              min="2020"
              max="2030"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Monat</label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i, 1).toLocaleString('de-DE', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">&nbsp;</label>
            <button
              onClick={loadReport}
              disabled={loading || !selectedDriver}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50"
              data-testid="load-report-button"
            >
              {loading ? 'Lädt...' : 'Anzeigen'}
            </button>
          </div>
        </div>
      </div>

      {/* Report */}
      {report && (
        <>
          {/* Summary */}
          <div className="dark-card rounded-lg shadow border p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">{report.driver_name}</h2>
                <p className="text-sm text-[var(--text-secondary)] mt-1">
                  {new Date(report.year, report.month - 1).toLocaleString('de-DE', { month: 'long', year: 'numeric' })}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm text-[var(--text-secondary)]">Monatsstunden</div>
                  <div className="text-4xl font-bold text-[var(--accent-primary)]">{report.total_hours} h</div>
                  <div className="text-xs text-[var(--text-secondary)] mt-1">{report.shift_count} Schichten</div>
                </div>
                <button
                  onClick={exportToCSV}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2"
                  data-testid="export-csv-button"
                >
                  <span>📊</span>
                  CSV Export
                </button>
              </div>
            </div>
          </div>

          {/* Incomplete Shifts Warning */}
          {report.incomplete_shifts && report.incomplete_shifts.length > 0 && (
            <div className="bg-[var(--warning-bg)] border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="font-semibold text-yellow-800 mb-2">
                ⚠️ Unvollständige Schichten ({report.incomplete_shifts.length})
              </div>
              <div className="text-sm text-[var(--warning-text)]">
                Folgende Schichten haben kein Ende-Zeitpunkt. Bitte manuell korrigieren.
              </div>
              <div className="mt-2 space-y-1">
                {report.incomplete_shifts.map((shift) => {
                  const startTime = new Date(shift.shift_start);
                  const now = new Date();
                  const minutesSinceStart = Math.floor((now - startTime) / 60000);
                  const hoursSinceStart = (minutesSinceStart / 60).toFixed(1);
                  
                  return (
                    <div key={shift.id} className="flex items-center justify-between dark-card p-2 rounded">
                      <div>
                        <div className="font-medium">
                          Offen seit {startTime.toLocaleString('de-DE')}
                        </div>
                        <div className="text-xs text-[var(--text-secondary)]">
                          Dauer bis jetzt: ca. {hoursSinceStart} h
                        </div>
                      </div>
                      <button
                        onClick={() => handleEditShift(shift)}
                        className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                      >
                        Korrigieren
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Shifts Table */}
          <div className="dark-card rounded-lg shadow border p-6 mb-6">
            <h3 className="text-lg font-bold mb-4">Schichten-Übersicht</h3>
            
            {report.shifts.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-secondary)]">Keine Schichten im gewählten Monat</div>
            ) : (
              <table className="w-full" data-testid="shifts-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Datum</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Start</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Ende</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Stunden</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-[var(--text-secondary)]">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {report.shifts.map((shift) => (
                    <tr key={shift.id} className="border-b hover:bg-[var(--bg-tertiary)]">
                      <td className="py-3 px-4 font-medium">
                        {new Date(shift.shift_start).toLocaleDateString('de-DE', {
                          weekday: 'short',
                          day: '2-digit',
                          month: '2-digit',
                        })}
                      </td>
                      <td className="py-3 px-4">
                        {new Date(shift.shift_start).toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="py-3 px-4">
                        {shift.shift_end
                          ? new Date(shift.shift_end).toLocaleTimeString('de-DE', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                      <td className="text-right py-3 px-4 font-bold">
                        {shift.duration_hours ? shift.duration_hours + ' h' : '—'}
                      </td>
                      <td className="text-center py-3 px-4">
                        {shift.is_open ? (
                          <span className="px-2 py-1 bg-yellow-100 text-[var(--warning-text)] text-xs rounded font-semibold">
                            Offen
                          </span>
                        ) : shift.edit_logs && shift.edit_logs.length > 0 ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                            Korrigiert
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-[var(--text-secondary)] text-xs rounded">
                            Abgeschlossen
                          </span>
                        )}
                      </td>
                      <td className="text-right py-3 px-4">
                        <button
                          onClick={() => handleEditShift(shift)}
                          className="text-sm text-[var(--accent-primary)] hover:underline"
                        >
                          Bearbeiten
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Audit Log */}
          {auditLog.length > 0 && (
            <div className="bg-[var(--bg-tertiary)] rounded-lg border p-6">
              <h3 className="text-lg font-bold mb-4">Änderungsprotokoll</h3>
              <div className="space-y-2">
                {auditLog.map((entry, idx) => (
                  <div key={idx} className="p-3 dark-card rounded border text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold">
                        {entry.edited_at ? new Date(entry.edited_at).toLocaleString('de-DE') : 'Unbekannt'}
                      </span>
                      <span className="text-[var(--text-secondary)]">{entry.edited_by}</span>
                    </div>
                    <div className="text-gray-700 mb-1">{entry.reason}</div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      <div>Alt: {entry.old_start ? new Date(entry.old_start).toLocaleString('de-DE') : '—'} bis {entry.old_end ? new Date(entry.old_end).toLocaleString('de-DE') : '—'}</div>
                      <div>Neu: {entry.new_start ? new Date(entry.new_start).toLocaleString('de-DE') : '—'} bis {entry.new_end ? new Date(entry.new_end).toLocaleString('de-DE') : '—'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editingShift && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setEditingShift(null)}
        >
          <div
            className="dark-card rounded-lg shadow-xl p-6 w-[500px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold mb-4">Schicht korrigieren</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">Start</label>
                <input
                  type="datetime-local"
                  value={editingShift.shift_start}
                  onChange={(e) => setEditingShift({ ...editingShift, shift_start: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ende</label>
                <input
                  type="datetime-local"
                  value={editingShift.shift_end}
                  onChange={(e) => setEditingShift({ ...editingShift, shift_end: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Grund der Änderung (Pflicht)</label>
                <textarea
                  value={editingShift.edit_reason}
                  onChange={(e) => setEditingShift({ ...editingShift, edit_reason: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                  placeholder="z.B. Vergessenes Logout"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setEditingShift(null)}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold"
              >
                Abbrechen
              </button>
              <button
                onClick={saveEdit}
                disabled={loading}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Speichere...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Stundenabrechnung;
